/**
 * Shared token refresh coordinator (CWE-362 fix — NPDIOTS-8800)
 *
 * Prevents race conditions between axiosInterceptor and idleTimeModal when
 * both attempt to consume the same single-use refresh token simultaneously.
 *
 * Guarantees:
 * - Only ONE network call fires regardless of how many callers request refresh
 * - All concurrent callers await the same in-flight Promise
 * - Lock is released after resolve OR reject so future refreshes work normally
 */

import { getInviteUserLoginEndPoint } from "../configuration";
import {
  CONTENT_TYPE_JSON,
  GRANT_TYPE_REFRESH_TOKEN,
  HEADER_CONTENT_TYPE,
  HTTP_POST,
} from "./constants/apiConstants";

let refreshPromise = null;

/**
 * Perform a token refresh with a shared lock.
 * Concurrent callers all receive the same Promise — only one network call fires.
 *
 * @param {string} refreshToken - The current refresh token from Redux store
 * @returns {Promise<{access_token: string, refresh_token: string, id_token: string}>}
 * @throws {Error} if refreshToken is empty, or if the server returns a non-OK response
 */
export const coordinatedRefresh = (refreshToken) => {
  if (!refreshToken) {
    return Promise.reject(new Error("refreshToken is empty — cannot refresh"));
  }

  // If a refresh is already in flight, all callers share it
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = fetch(getInviteUserLoginEndPoint(), {
    method: HTTP_POST,
    headers: {
      [HEADER_CONTENT_TYPE]: CONTENT_TYPE_JSON,
    },
    body: JSON.stringify({
      grantType: GRANT_TYPE_REFRESH_TOKEN,
      refreshToken,
    }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .finally(() => {
      // Release lock whether success or failure, so next refresh attempt works
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Reset refresh state — only for testing.
 * Do NOT call in production code.
 */
export const resetRefreshState = () => {
  refreshPromise = null;
};
